import { useEffect, useRef, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { toast } from "sonner";
import { Share2, Copy, Download, X, QrCode } from "lucide-react";
import { SITE_URL } from "@/lib/config";

const STRIP = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content", "fbclid", "gclid", "ref", "session", "token"];

export function getCanonicalShareUrl() {
  try {
    const { pathname, search } = window.location;
    const params = new URLSearchParams(search);
    STRIP.forEach((p) => params.delete(p));
    const q = params.toString();
    return `${SITE_URL}${pathname}${q ? `?${q}` : ""}`;
  } catch {
    return SITE_URL + "/";
  }
}

function slugForFile() {
  const p = window.location.pathname.replace(/^\/+|\/+$/g, "").replace(/[^a-z0-9]+/gi, "-");
  return `veterans-pwc-${p || "home"}-qr.png`;
}

export function SharePageModal({ onClose }) {
  const url = getCanonicalShareUrl();
  const canvasWrap = useRef(null);
  const [qrError, setQrError] = useState(false);
  const canNativeShare = typeof navigator !== "undefined" && !!navigator.share;

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(url); toast.success("Link copied!"); }
    catch { toast.error("Copy not supported — select the link to copy."); }
  };
  const nativeShare = async () => {
    try { await navigator.share({ title: "Veterans Powerwash and Cleaning Services", text: "Check out Veterans PWC", url }); }
    catch { /* user cancelled */ }
  };
  const downloadQr = () => {
    const canvas = canvasWrap.current?.querySelector("canvas");
    if (!canvas) { toast.error("Unable to download QR code."); return; }
    const a = document.createElement("a");
    a.href = canvas.toDataURL("image/png");
    a.download = slugForFile();
    a.click();
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center px-4" role="dialog" aria-modal="true" aria-label="Share this page" data-testid="share-page-modal">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl w-full max-w-sm p-6 text-center">
        <button onClick={onClose} aria-label="Close" className="absolute top-3 right-3 text-gray-400 hover:text-[var(--navy)]" data-testid="share-close"><X className="w-5 h-5" /></button>
        <h2 className="font-head font-extrabold text-lg text-[var(--navy)] uppercase tracking-tight">Share This Page</h2>
        <p className="text-xs text-[var(--text-muted)] mt-1">Scan this code to open Veterans PWC</p>

        <div ref={canvasWrap} className="mt-4 mx-auto w-fit bg-white p-4 rounded-xl border border-gray-100" data-testid="share-qr">
          {qrError ? (
            <div className="w-[240px] h-[240px] flex items-center justify-center text-sm text-red-600">Unable to generate QR code.</div>
          ) : (
            <QRCodeCanvas value={url} size={240} includeMargin level="M" onError={() => setQrError(true)} data-testid="share-qr-canvas" />
          )}
        </div>

        <p className="text-[11px] text-gray-400 mt-3 break-all" data-testid="share-url">{url}</p>

        <div className="mt-4 grid grid-cols-1 gap-2">
          <button onClick={copyLink} className="btn-lift bg-[var(--navy)] text-white font-semibold py-2.5 rounded-full flex items-center justify-center gap-2" data-testid="share-copy"><Copy className="w-4 h-4" /> Copy Link</button>
          {canNativeShare && <button onClick={nativeShare} className="btn-lift bg-[var(--red)] text-white font-semibold py-2.5 rounded-full flex items-center justify-center gap-2" data-testid="share-native"><Share2 className="w-4 h-4" /> Share</button>}
          <button onClick={downloadQr} className="btn-lift bg-white border border-gray-200 text-[var(--navy)] font-semibold py-2.5 rounded-full flex items-center justify-center gap-2" data-testid="share-download"><Download className="w-4 h-4" /> Download QR</button>
        </div>
      </div>
    </div>
  );
}

export default function SharePageButton({ variant = "icon", className = "" }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      {variant === "full" ? (
        <button onClick={() => setOpen(true)} className={`flex items-center gap-2 font-medium text-[var(--navy)] ${className}`} aria-label="Share this page" data-testid="share-page-btn">
          <Share2 className="w-4 h-4" /> Share This Page
        </button>
      ) : (
        <button onClick={() => setOpen(true)} className={`p-2.5 text-[var(--navy)] hover:text-[var(--action)] ${className}`} aria-label="Share this page" title="Share this page" data-testid="share-page-btn">
          <Share2 className="w-5 h-5" />
        </button>
      )}
      {open && <SharePageModal onClose={() => setOpen(false)} />}
    </>
  );
}
